import React, { useEffect, useState, useRef, useMemo } from "react";
import { db, auth, storage } from "../firebase"; 
import { Trash2, PlusCircle, Wallet, ChevronDown, Filter, Image as ImageIcon, X, ChevronLeft, ChevronRight } from "lucide-react";
import {
  collection,
  addDoc,
  getDocs,
  doc,
  getDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";

// --- COLUMN FILTER COMPONENT ---
const TableFilter = ({ column, uniqueValues, activeFilters, onFilterChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const filterRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterRef.current && !filterRef.current.contains(event.target)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleFilter = (val) => {
    const current = activeFilters[column] || [];
    const updated = current.includes(val) ? current.filter(v => v !== val) : [...current, val];
    onFilterChange(column, updated);
  };

  return (
    <div style={{ position: 'relative', display: 'inline-block', marginLeft: '6px' }} ref={filterRef}>
      <Filter 
        size={11} 
        style={{ cursor: 'pointer', color: activeFilters[column]?.length ? '#38bdf8' : '#475569' }} 
        onClick={() => setIsOpen(!isOpen)} 
      />
      {isOpen && (
        <div style={styles.filterDropdown}>
          <div style={styles.filterOption} onClick={() => onFilterChange(column, [])}>
            <span style={{ fontSize: '10px', color: '#38bdf8', fontWeight: 800 }}>CLEAR ALL</span>
          </div>
          <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
            {uniqueValues.sort().map(val => (
              <div key={val} style={styles.filterOption} onClick={() => toggleFilter(val)}>
                <input type="checkbox" checked={(activeFilters[column] || []).includes(val)} readOnly style={{ pointerEvents: 'none' }} />
                <span style={{ marginLeft: '8px', whiteSpace: 'nowrap' }}>{val}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// --- CUSTOM SELECT COMPONENT ---
const CustomSelect = ({ label, options, value, onChange, placeholder = "Select Option" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedOption = options.find(opt => (opt.id || opt) === value);
  const displayLabel = selectedOption ? (selectedOption.formattedLabel || selectedOption) : placeholder;

  return (
    <div style={styles.inputGroup} ref={dropdownRef}>
      <label style={styles.label}>{label}</label>
      <div style={styles.customSelectWrapper} onClick={() => setIsOpen(!isOpen)}>
        <div style={styles.customSelectTrigger}>
          <span style={{ color: value ? "#fff" : "#64748b" }}>{displayLabel}</span>
          <ChevronDown size={16} style={{ color: "#38bdf8", transform: isOpen ? 'rotate(180deg)' : 'rotate(0)', transition: '0.2s' }} />
        </div>
        {isOpen && (
          <div style={styles.customSelectOptions}>
            {options.map((opt, index) => {
              const val = opt.id || opt;
              const lbl = opt.formattedLabel || opt;
              return (
                <div 
                  key={index} 
                  style={styles.customOption} 
                  onClick={(e) => {
                    e.stopPropagation();
                    onChange(val);
                    setIsOpen(false);
                  }}
                >
                  {lbl}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default function AddTrade() {
  const [accounts, setAccounts] = useState([]);
  const [entryType, setEntryType] = useState("trade");
  const [recentTx, setRecentTx] = useState([]);
  const [activeFilters, setActiveFilters] = useState({});
  
  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 30;

  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  const [trade, setTrade] = useState({
    accountId: "", instrument: "", market: "", session: "", timeframe: "",
    rr: "1.5", result: "", pnl: "", reason: "", 
    mentalMistake: "Followed Plan",
    mindsetState: "Disciplined",
    tradeRating: 5,
    date: new Date().toISOString().slice(0, 10)
  });

  const [withdrawal, setWithdrawal] = useState({
    accountId: "", amount: "", date: new Date().toISOString().slice(0, 10)
  });

  const withdrawalEligibleAccounts = accounts.filter(acc => 
    acc.phase?.toLowerCase() === "funded" || acc.phase?.toLowerCase() === "personal"
  );

  useEffect(() => {
    const fetchAccounts = async () => {
      if (!auth.currentUser) return;
      const q = query(collection(db, "users", auth.currentUser.uid, "accounts"), orderBy("name"));
      const snap = await getDocs(q);
      const accs = snap.docs.map(d => {
        const data = d.data();
        const formattedLabel = `${data.name || "Broker"} — ${data.accountCode || "—"} | ${data.currency || "₹"}${Number(data.size || 0).toLocaleString()} (${data.phase || "Live"})`;
        return { id: d.id, ...data, formattedLabel };
      });
      setAccounts(accs);
      loadRecentTransactions(accs);
    };
    fetchAccounts();
  }, []);

  const loadRecentTransactions = async (accs) => {
    if (!auth.currentUser) return;
    const uid = auth.currentUser.uid;
    let rows = [];
    for (const acc of accs) {
      const accRef = doc(db, "users", uid, "accounts", acc.id);
      const tradesSnap = await getDocs(collection(accRef, "trades"));
      tradesSnap.forEach(d => {
        rows.push({
          id: d.id, date: d.data().date,
          day: new Date(d.data().date).toLocaleDateString("en-US", { weekday: "short" }).toUpperCase(),
          broker: acc.name, 
          accId: acc.accountCode || "—",
          phase: acc.phase || "—",
          currency: acc.currency || "₹",
          transactionType: "TRADE", 
          amount: d.data().pnl,
          path: `users/${uid}/accounts/${acc.id}/trades/${d.id}`,
          createdAt: d.data().createdAt?.toDate?.() || new Date()
        });
      });
      const wdSnap = await getDocs(collection(accRef, "withdrawals"));
      wdSnap.forEach(d => {
        rows.push({
          id: d.id, date: d.data().date,
          day: new Date(d.data().date).toLocaleDateString("en-US", { weekday: "short" }).toUpperCase(),
          broker: acc.name,
          accId: acc.accountCode || "—",
          phase: acc.phase || "—",
          currency: acc.currency || "₹",
          transactionType: "WITHDRAWAL", 
          amount: d.data().amount,
          path: `users/${uid}/accounts/${acc.id}/withdrawals/${d.id}`,
          createdAt: d.data().createdAt?.toDate?.() || new Date()
        });
      });
    }
    rows.sort((a, b) => b.createdAt - a.createdAt);
    setRecentTx(rows);
  };

  const filteredTx = useMemo(() => {
    return recentTx.filter(tx => {
      return Object.keys(activeFilters).every(column => {
        if (!activeFilters[column] || activeFilters[column].length === 0) return true;
        const val = column === 'type' ? tx.transactionType : tx[column]?.toString();
        return activeFilters[column].includes(val);
      });
    });
  }, [recentTx, activeFilters]);

  // Pagination Logic
  const totalPages = Math.ceil(filteredTx.length / itemsPerPage);
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredTx.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredTx, currentPage]);

  const getUniqueValues = (column) => {
    const vals = recentTx.map(tx => column === 'type' ? tx.transactionType : tx[column]);
    return [...new Set(vals.filter(Boolean))];
  };

  const handleFilterChange = (col, vals) => {
    setActiveFilters(prev => ({ ...prev, [col]: vals }));
    setCurrentPage(1); // Reset to first page on filter
  };

  const deleteTx = async (tx) => {
    if (!window.confirm("Delete this transaction permanently?")) return;
    try {
        const accountPathParts = tx.path.split('/');
        const accountId = accountPathParts[3];
        const accountRef = doc(db, "users", auth.currentUser.uid, "accounts", accountId);

        const accSnap = await getDoc(accountRef);
        if (accSnap.exists()) {
          await updateDoc(accountRef, { 
            currentPnL: (accSnap.data().currentPnL || 0) - tx.amount 
          });
        }

        await deleteDoc(doc(db, tx.path));
        setRecentTx(prev => prev.filter(r => r.id !== tx.id));
    } catch (e) {
        alert("Error deleting transaction");
    }
  };

  const handleChange = e => setTrade({ ...trade, [e.target.name]: e.target.value });
  const handleDropdownChange = (name, val) => setTrade({ ...trade, [name]: val });

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const saveTrade = async () => {
    const requiredFields = ['accountId', 'instrument', 'market', 'session', 'timeframe', 'rr', 'result', 'pnl'];
    if (requiredFields.some(f => !trade[f])) return alert("Please fill in all fields marked with *");
    
    setIsUploading(true);
    let finalImageUrl = "";

    try {
      if (imageFile) {
        const storageRef = ref(storage, `trade_proofs/${auth.currentUser.uid}/${Date.now()}_${imageFile.name}`);
        const snapshot = await uploadBytes(storageRef, imageFile);
        finalImageUrl = await getDownloadURL(snapshot.ref);
      }

      let pnlValue = trade.result.toLowerCase() === "loss" ? -Math.abs(Number(trade.pnl)) : Math.abs(Number(trade.pnl));
      const userId = auth.currentUser.uid;
      const accountRef = doc(db, "users", userId, "accounts", trade.accountId);
      
      await addDoc(collection(accountRef, "trades"), { 
        ...trade, 
        pnl: pnlValue, 
        rr: Number(trade.rr), 
        tradeRating: Number(trade.tradeRating), 
        tradeImageUrl: finalImageUrl, 
        createdAt: new Date() 
      });

      const accSnap = await getDoc(accountRef);
      if (accSnap.exists()) {
        await updateDoc(accountRef, { currentPnL: (accSnap.data().currentPnL || 0) + pnlValue });
      }
      
      setTrade({ ...trade, pnl: "", instrument: "", rr: "1.5", reason: "", result: "", mentalMistake: "Followed Plan", mindsetState: "Disciplined", tradeRating: 5 });
      setImageFile(null);
      setImagePreview(null);
      loadRecentTransactions(accounts);
      alert("Trade saved successfully!");
    } catch (error) {
      console.error("Error saving trade:", error);
      alert(`Failed to save trade: ${error.message}`);
    } finally {
      setIsUploading(false);
    }
  };

  const saveWithdrawal = async () => {
    const { accountId, amount, date } = withdrawal;
    if (!accountId || !amount || !date) return alert("Please fill in all withdrawal fields.");

    setIsUploading(true);
    const userId = auth.currentUser.uid;
    const accountRef = doc(db, "users", userId, "accounts", accountId);

    try {
      const withdrawalValue = -Math.abs(Number(amount)); 

      await addDoc(collection(accountRef, "withdrawals"), {
        amount: withdrawalValue,
        date: date,
        createdAt: new Date(),
        transactionType: "WITHDRAWAL"
      });

      const accSnap = await getDoc(accountRef);
      if (accSnap.exists()) {
        await updateDoc(accountRef, { 
          currentPnL: (accSnap.data().currentPnL || 0) + withdrawalValue 
        });
      }

      setWithdrawal({ ...withdrawal, amount: "" });
      loadRecentTransactions(accounts);
      alert("Withdrawal successfully logged and balance updated!");
    } catch (error) {
      console.error("Error logging withdrawal:", error);
      alert("Failed to log withdrawal.");
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div style={styles.wrapper}>
      <style>{customStyles}</style>
      <div style={styles.innerContainer}>
        <h2 style={styles.title}>Add New Entry</h2>
        <div style={styles.mainLayout}>
          
          <div style={styles.formSection}>
            <div style={styles.tabContainer}>
              <div onClick={() => setEntryType("trade")} style={{ ...styles.tab, borderBottomColor: entryType === "trade" ? "#38bdf8" : "transparent", color: entryType === "trade" ? "#38bdf8" : "#94a3b8" }}>
                <PlusCircle size={16} /> Log Trade
              </div>
              <div onClick={() => setEntryType("withdrawal")} style={{ ...styles.tab, borderBottomColor: entryType === "withdrawal" ? "#38bdf8" : "transparent", color: entryType === "withdrawal" ? "#38bdf8" : "#94a3b8" }}>
                <Wallet size={16} /> Withdrawal
              </div>
            </div>

            <div style={styles.formCard}>
              {entryType === "trade" ? (
                <div style={styles.form}>
                  <CustomSelect label="TRADING ACCOUNT *" options={accounts} value={trade.accountId} onChange={(val) => handleDropdownChange('accountId', val)} />
                  <div style={styles.gridRow}>
                    <div style={styles.gridCol}><label style={styles.label}>DATE *</label><input type="date" name="date" value={trade.date} onChange={handleChange} style={styles.input} /></div>
                    <div style={styles.gridCol}><label style={styles.label}>INSTRUMENT *</label><input placeholder="e.g. NAS100" style={styles.input} name="instrument" value={trade.instrument} onChange={handleChange} /></div>
                  </div>
                  <div style={styles.gridRow}>
                    <div style={styles.gridCol}><CustomSelect label="MARKET *" options={["Forex", "Futures", "Crypto", "Stocks"]} value={trade.market} onChange={(val) => handleDropdownChange('market', val)} /></div>
                    <div style={styles.gridCol}><CustomSelect label="SESSION *" options={["Asia", "London", "New York"]} value={trade.session} onChange={(val) => handleDropdownChange('session', val)} /></div>
                  </div>
                  <div style={styles.gridRow}>
                    <div style={styles.gridCol}><CustomSelect label="TIMEFRAME *" options={["1m", "5m", "15m", "1h", "4h", "Daily"]} value={trade.timeframe} onChange={(val) => handleDropdownChange('timeframe', val)} /></div>
                    <div style={styles.gridCol}><label style={styles.label}>R : R *</label><input type="number" step="0.1" style={styles.input} name="rr" value={trade.rr} onChange={handleChange} /></div>
                  </div>
                  <div style={styles.gridRow}>
                    <div style={styles.gridCol}><CustomSelect label="RESULT *" options={["Profit", "Loss"]} value={trade.result} onChange={(val) => handleDropdownChange('result', val)} /></div>
                    <div style={styles.gridCol}><label style={styles.label}>PNL AMOUNT *</label><input type="number" placeholder="0.00" style={styles.input} name="pnl" value={trade.pnl} onChange={handleChange} /></div>
                  </div>

                  <div style={styles.gridRow}>
                    <div style={styles.gridCol}>
                      <CustomSelect label="MINDSET STATE *" options={["Disciplined", "Calm / Neutral", "High Conviction", "Anxious / Nervous", "Bored"]} value={trade.mindsetState} onChange={(val) => handleDropdownChange('mindsetState', val)} />
                    </div>
                    <div style={styles.gridCol}>
                      <CustomSelect label="MENTAL MISTAKE *" options={["Followed Plan", "FOMO", "Overtrading", "Early Exit", "Revenge Trading", "Chasing"]} value={trade.mentalMistake} onChange={(val) => handleDropdownChange('mentalMistake', val)} />
                    </div>
                  </div>
                  
                  <div style={styles.inputGroup}>
                    <label style={styles.label}>RATE TRADE: {trade.tradeRating}/10 *</label>
                    <input type="range" min="1" max="10" name="tradeRating" value={trade.tradeRating} onChange={handleChange} className="rating-slider" />
                  </div>

                  <div style={styles.inputGroup}>
                    <label style={styles.label}>ADD TRADE IMAGE</label>
                    <div style={styles.imageUploadBox}>
                      {!imagePreview ? (
                        <label style={styles.imagePlaceholder}>
                          <input type="file" accept="image/*" onChange={handleImageChange} style={{ display: 'none' }} />
                          <ImageIcon size={20} style={{ marginBottom: '4px' }} />
                          <span>Click to upload execution screenshot</span>
                        </label>
                      ) : (
                        <div style={styles.previewContainer}>
                          <img src={imagePreview} alt="Trade Preview" style={styles.imagePreviewImg} />
                          <button onClick={() => {setImageFile(null); setImagePreview(null);}} style={styles.removeImgBtn}><X size={14} /></button>
                        </div>
                      )}
                    </div>
                  </div>

                  <div style={styles.inputGroup}>
                    <label style={styles.label}>TRADE REASON (OPTIONAL)</label>
                    <textarea name="reason" placeholder="Strategy notes..." value={trade.reason} onChange={handleChange} style={styles.textarea} />
                  </div>

                  <button style={{...styles.saveBtn, opacity: isUploading ? 0.6 : 1}} onClick={saveTrade} disabled={isUploading}>
                    {isUploading ? "Uploading..." : "Save Transaction"}
                  </button>
                </div>
              ) : (
                <div style={styles.form}>
                  <CustomSelect label="ACCOUNT (FUNDED/PERSONAL ONLY) *" options={withdrawalEligibleAccounts} value={withdrawal.accountId} onChange={(val) => setWithdrawal({...withdrawal, accountId: val})} placeholder="Select Funded Account" />
                  <div style={styles.gridRow}>
                    <div style={styles.gridCol}><label style={styles.label}>DATE *</label><input type="date" value={withdrawal.date} onChange={(e) => setWithdrawal({...withdrawal, date: e.target.value})} style={styles.input} /></div>
                    <div style={styles.gridCol}><label style={styles.label}>AMOUNT *</label><input type="number" placeholder="0.00" style={styles.input} value={withdrawal.amount} onChange={(e) => setWithdrawal({...withdrawal, amount: e.target.value})} /></div>
                  </div>
                  <button 
                    style={{...styles.saveBtn, opacity: isUploading ? 0.6 : 1}} 
                    onClick={saveWithdrawal} 
                    disabled={isUploading}
                  >
                    {isUploading ? "Logging..." : "Log Withdrawal"}
                  </button>
                </div>
              )}
            </div>
          </div>

          <div style={styles.divider}></div>

          <div style={styles.tableSection}>
            <div style={styles.tableCard}>
              <h3 style={styles.cardHeader}>Your Recent Transactions</h3>
              <div style={styles.tableHeader}>
                <div style={styles.headerItem}>DATE <TableFilter column="date" uniqueValues={getUniqueValues('date')} activeFilters={activeFilters} onFilterChange={handleFilterChange} /></div>
                <div style={styles.headerItem}>DAY <TableFilter column="day" uniqueValues={getUniqueValues('day')} activeFilters={activeFilters} onFilterChange={handleFilterChange} /></div>
                <div style={styles.headerItem}>BROKER <TableFilter column="broker" uniqueValues={getUniqueValues('broker')} activeFilters={activeFilters} onFilterChange={handleFilterChange} /></div>
                <div style={styles.headerItem}>ACC ID <TableFilter column="accId" uniqueValues={getUniqueValues('accId')} activeFilters={activeFilters} onFilterChange={handleFilterChange} /></div>
                <div style={styles.headerItem}>PHASE <TableFilter column="phase" uniqueValues={getUniqueValues('phase')} activeFilters={activeFilters} onFilterChange={handleFilterChange} /></div>
                <div style={styles.headerItem}>TYPE <TableFilter column="type" uniqueValues={getUniqueValues('type')} activeFilters={activeFilters} onFilterChange={handleFilterChange} /></div>
                <div style={styles.headerItem}>PNL</div>
                <div style={styles.headerItem}>DEL</div>
              </div>
              <div className="custom-scrollbar" style={styles.scrollContainer}>
                {paginatedData.map(tx => (
                  <div key={tx.id} className="table-row-hover" style={styles.tableRow}>
                    <div style={styles.rowItem}>{tx.date}</div>
                    <div style={{ ...styles.rowItem, color: "#64748b", fontWeight: "bold" }}>{tx.day}</div>
                    <div style={{ ...styles.rowItem, color: "#fff", fontWeight: 700 }}>{tx.broker}</div>
                    <div style={{ ...styles.rowItem, color: "#94a3b8" }}>{tx.accId}</div>
                    <div style={{ ...styles.rowItem, fontSize: '11px', color: '#64748b' }}>{tx.phase}</div>
                    <div style={{ ...styles.rowItem, fontSize: '11px', fontWeight: 700, color: tx.transactionType === "TRADE" ? "#38bdf8" : "#fbbf24" }}>{tx.transactionType}</div>
                    <div style={{ ...styles.rowItem, color: tx.amount > 0 ? "#22c55e" : tx.amount < 0 ? "#ef4444" : "#94a3b8", fontWeight: 800 }}>
                      {tx.amount < 0 ? `-${tx.currency}${Math.abs(tx.amount).toLocaleString()}` : `${tx.currency}${tx.amount.toLocaleString()}`}
                    </div>
                    <div style={{ ...styles.rowItem, display: "flex", justifyContent: "center" }} onClick={() => deleteTx(tx)}>
                      <Trash2 size={16} className="delete-btn" />
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div style={styles.paginationBar}>
                  <button 
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))} 
                    disabled={currentPage === 1}
                    style={{...styles.pageBtn, opacity: currentPage === 1 ? 0.3 : 1}}
                  >
                    <ChevronLeft size={16} />
                  </button>
                  <span style={styles.pageInfo}>Page {currentPage} of {totalPages}</span>
                  <button 
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} 
                    disabled={currentPage === totalPages}
                    style={{...styles.pageBtn, opacity: currentPage === totalPages ? 0.3 : 1}}
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const customStyles = `
  html, body { margin: 0 !important; padding: 0 !important; background-color: #000; }
  ::-webkit-scrollbar { width: 4px; }
  ::-webkit-scrollbar-thumb { background: #1e293b; border-radius: 10px; }
  ::-webkit-scrollbar-thumb:hover { background: #38bdf8; }
  .table-row-hover:hover { background: rgba(255, 255, 255, 0.02); }
  .delete-btn { color: #475569; cursor: pointer; transition: color 0.2s; }
  .delete-btn:hover { color: #ef4444; }
  input[type="date"]::-webkit-calendar-picker-indicator { filter: invert(1); opacity: 0.5; cursor: pointer; }
  .rating-slider { -webkit-appearance: none; width: 100%; height: 6px; border-radius: 10px; background: linear-gradient(to right, #ef4444 0%, #f59e0b 50%, #22c55e 100%); outline: none; margin: 15px 0; }
  .rating-slider::-webkit-slider-thumb { -webkit-appearance: none; width: 18px; height: 18px; background: #38bdf8; border-radius: 50%; cursor: pointer; border: 3px solid #0d1117; box-shadow: 0 0 10px rgba(56, 189, 248, 0.4); }
`;

const styles = {
  wrapper: { width: "100%", minHeight: "100vh", display: "flex", flexDirection: "column" },
  innerContainer: { width: "100%", flex: 1, color: "#fff", padding: "0 20px 40px 20px" },
  title: { fontSize: 32, fontWeight: 800, marginBottom: 30, letterSpacing: "-1px" },
  mainLayout: { display: "flex", gap: "24px", width: "100%", alignItems: "flex-start" },
  formSection: { flex: "0 0 420px" },
  divider: { width: "1px", background: "#1e293b", alignSelf: "stretch" },
  tableSection: { flex: 1, minWidth: 0 },
  tabContainer: { display: "flex", marginBottom: "20px", borderBottom: "1px solid #1e293b" },
  tab: { flex: 1, display: "flex", justifyContent: "center", alignItems: "center", gap: 8, padding: "14px", cursor: "pointer", fontSize: 13, fontWeight: 700, borderBottom: "2px solid transparent" },
  formCard: { background: "rgba(13, 17, 23, 0.6)", borderRadius: "12px", padding: "24px", border: "1px solid #1e293b" },
  tableCard: { background: "rgba(13, 17, 23, 0.4)", border: "1px solid #1e293b", borderRadius: "12px", overflow: 'hidden', display: 'flex', flexDirection: 'column' },
  cardHeader: { fontSize: 18, fontWeight: 700, padding: "20px", borderBottom: "1px solid #1e293b", margin: 0, color: "#f8fafc", textAlign: "center" },
  tableHeader: { display: "grid", gridTemplateColumns: "1.2fr 0.8fr 1.5fr 1fr 1fr 1fr 1.2fr 0.5fr", background: "rgba(255,255,255,0.02)", color: "#475569", padding: "12px 24px", fontSize: 10, fontWeight: 800, letterSpacing: '1px' },
  headerItem: { textAlign: "left", display: 'flex', alignItems: 'center' },
  scrollContainer: { maxHeight: "calc(100vh - 310px)", overflowY: "auto" },
  tableRow: { display: "grid", gridTemplateColumns: "1.2fr 0.8fr 1.5fr 1fr 1fr 1fr 1.2fr 0.5fr", padding: "16px 24px", borderBottom: "1px solid #1e293b", alignItems: "center" },
  rowItem: { textAlign: "left", fontSize: 13 },
  form: { display: "flex", flexDirection: "column", gap: 16 },
  gridRow: { display: "flex", gap: "12px" }, 
  gridCol: { flex: 1 }, 
  inputGroup: { display: "flex", flexDirection: "column", gap: 6 },
  label: { fontSize: 10, fontWeight: 800, color: "#475569", letterSpacing: '0.5px' },
  input: { background: "#0d1117", border: "1px solid #1e293b", borderRadius: "6px", padding: "12px", color: "#fff", outline: "none", width: "100%", boxSizing: "border-box" },
  textarea: { background: "#0d1117", border: "1px solid #1e293b", borderRadius: "6px", padding: "12px", color: "#fff", outline: "none", width: "100%", height: "80px", resize: "none", boxSizing: "border-box" },
  saveBtn: { background: "#38bdf8", color: "#000", padding: "14px", borderRadius: "8px", fontWeight: 800, cursor: "pointer", border: "none", width: "100%", fontSize: "14px", transition: '0.2s' },
  customSelectWrapper: { position: 'relative' },
  customSelectTrigger: { background: "#0d1117", border: "1px solid #1e293b", borderRadius: "6px", padding: "12px", color: "#fff", display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  customSelectOptions: { position: 'absolute', top: '110%', left: 0, right: 0, background: '#0d1117', border: '1px solid #1e293b', borderRadius: '6px', zIndex: 100, overflow: 'hidden' },
  customOption: { padding: '12px', fontSize: '13px', cursor: 'pointer', borderBottom: '1px solid #1e293b' },
  filterDropdown: { position: 'absolute', top: '100%', left: 0, background: '#0d1117', border: '1px solid #1e293b', borderRadius: '8px', padding: '8px 0', zIndex: 1000, minWidth: '160px', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.5)' },
  filterOption: { padding: '8px 16px', fontSize: '11px', display: 'flex', alignItems: 'center', cursor: 'pointer', color: '#94a3b8', transition: '0.2s' },
  imageUploadBox: { background: "#0d1117", border: "1px dashed #1e293b", borderRadius: "6px", minHeight: "80px", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", overflow: "hidden" },
  imagePlaceholder: { display: "flex", flexDirection: "column", alignItems: "center", color: "#475569", fontSize: "11px", cursor: "pointer", width: "100%", padding: "20px" },
  previewContainer: { position: "relative", width: "100%", height: "150px" },
  imagePreviewImg: { width: "100%", height: "100%", objectFit: "cover" },
  removeImgBtn: { position: "absolute", top: "5px", right: "5px", background: "rgba(0,0,0,0.7)", color: "#fff", border: "none", borderRadius: "4px", padding: "4px", cursor: "pointer" },
  
  // New Pagination Styles
  paginationBar: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px', padding: '16px', borderTop: '1px solid #1e293b', background: 'rgba(255,255,255,0.01)' },
  pageBtn: { background: 'none', border: '1px solid #1e293b', color: '#38bdf8', padding: '6px', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center' },
  pageInfo: { fontSize: '12px', fontWeight: '700', color: '#94a3b8' }
};